/**
 * Activity Tracker Hook
 * Tracks user actions across all pages for audit and troubleshooting
 * Multi-tenant support with automatic session management
 */
import { useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import apiClient from '../services/api/client';

// Action types
export const ActionTypes = {
  PAGE_VIEW: 'page_view',
  CLICK: 'click',
  FORM_SUBMIT: 'form_submit',
  BUTTON_CLICK: 'button_click',
  SCAN_START: 'scan_start',
  SCAN_STOP: 'scan_stop',
  SEARCH: 'search',
  FILTER: 'filter',
  EXPORT: 'export',
  DOWNLOAD: 'download',
  UPLOAD: 'upload',
  ERROR: 'error',
  LOGIN: 'login',
  LOGOUT: 'logout',
  SETTING_CHANGE: 'setting_change',
  API_CALL: 'api_call',
};

// Action categories
export const ActionCategories = {
  NAVIGATION: 'navigation',
  SCAN: 'scan',
  EXPLOITATION: 'exploitation',
  SETTINGS: 'settings',
  AUTH: 'auth',
  REPORT: 'report',
  DATA: 'data',
  API: 'api',
};

// Page title mapping
const PAGE_TITLES = {
  '/': 'Dashboard',
  '/dashboard': 'Dashboard',
  '/scans': 'Scans',
  '/scans/new': 'Nouveau scan',
  '/workspaces': 'Workspaces',
  '/vulnerabilities': 'Vulnerabilites',
  '/sessions': 'Sessions',
  '/credentials': 'Credentials',
  '/reports': 'Rapports',
  '/settings': 'Paramètres',
  '/attack': 'Attack Builder',
  '/mitre': 'MITRE Navigator',
  '/network-map': 'Network Map',
  '/admin': 'Administration',
  '/account': 'Mon Compte',
  '/support': 'Support',
  '/notifications': 'Notifications',
  '/orchestrator': 'Orchestrateur',
  '/subdomains': 'Subdomains',
  '/ad-pentest': 'Active Directory',
  '/llm-config': 'LLM Configuration',
  '/monitoring': 'Monitoring',
};

// Buffer for batch tracking
let actionBuffer = [];
let flushTimeout = null;
const BUFFER_SIZE = 10;
const FLUSH_INTERVAL = 30000; // 30 seconds

/**
 * Flush buffered actions to the server
 */
const flushActions = async () => {
  if (actionBuffer.length === 0) return;

  const actionsToSend = [...actionBuffer];
  actionBuffer = [];

  try {
    const token = localStorage.getItem('access_token');
    if (!token) return;

    await apiClient.post('/history/batch', actionsToSend);
  } catch (error) {
    // Don't retry on auth errors (401/403) - token is invalid, no point retrying
    if (error.response && (error.response.status === 401 || error.response.status === 403)) {
      return;
    }
    console.warn('Failed to track actions:', error);
    // Re-add failed actions to buffer (limited)
    actionBuffer = [...actionsToSend.slice(-5), ...actionBuffer].slice(0, BUFFER_SIZE * 2);
  }
};

/**
 * Schedule flush with debouncing
 */
const scheduleFlush = () => {
  if (flushTimeout) clearTimeout(flushTimeout);
  flushTimeout = setTimeout(flushActions, FLUSH_INTERVAL);
};

/**
 * Track a single action
 */
export const trackAction = (action) => {
  const token = localStorage.getItem('access_token');
  if (!token) return; // Don't track if not logged in

  actionBuffer.push({
    ...action,
    timestamp: new Date().toISOString(),
  });

  if (actionBuffer.length >= BUFFER_SIZE) {
    flushActions();
  } else {
    scheduleFlush();
  }
};

/**
 * Track page view
 */
export const trackPageView = (page, pageTitle) => {
  trackAction({
    action_type: ActionTypes.PAGE_VIEW,
    action_category: ActionCategories.NAVIGATION,
    page,
    page_title: pageTitle || PAGE_TITLES[page] || page,
  });
};

/**
 * Track button/action click
 */
export const trackClick = (buttonName, page, details = {}) => {
  trackAction({
    action_type: ActionTypes.BUTTON_CLICK,
    action_category: ActionCategories.DATA,
    page,
    description: `Clicked: ${buttonName}`,
    details: { button: buttonName, ...details },
  });
};

/**
 * Track scan action
 */
export const trackScanAction = (actionType, scanId, scanName, details = {}) => {
  trackAction({
    action_type: `scan_${actionType}`,
    action_category: ActionCategories.SCAN,
    resource_type: 'scan',
    resource_id: scanId,
    resource_name: scanName,
    description: `Scan ${actionType}: ${scanName || scanId}`,
    details,
  });
};

/**
 * Track error
 */
export const trackError = (errorMessage, page, details = {}) => {
  trackAction({
    action_type: ActionTypes.ERROR,
    action_category: ActionCategories.DATA,
    page,
    error: true,
    error_message: errorMessage,
    description: `Error: ${errorMessage}`,
    details,
  });

  // Flush immediately on error
  flushActions();
};

/**
 * Track search/filter action
 */
export const trackSearch = (query, page, filters = {}) => {
  trackAction({
    action_type: ActionTypes.SEARCH,
    action_category: ActionCategories.DATA,
    page,
    description: `Search: ${query}`,
    details: { query, filters },
  });
};

/**
 * Track setting change
 */
export const trackSettingChange = (settingName, oldValue, newValue, page = '/settings') => {
  trackAction({
    action_type: ActionTypes.SETTING_CHANGE,
    action_category: ActionCategories.SETTINGS,
    page,
    description: `Setting changed: ${settingName}`,
    details: { setting: settingName, old_value: oldValue, new_value: newValue },
  });
};

/**
 * Track API call (for important operations)
 */
export const trackApiCall = (method, endpoint, statusCode, durationMs, error = null) => {
  trackAction({
    action_type: ActionTypes.API_CALL,
    action_category: ActionCategories.API,
    method,
    endpoint,
    status_code: String(statusCode),
    duration_ms: String(durationMs),
    error: !!error,
    error_message: error,
  });
};

/**
 * React hook for automatic page view tracking
 */
export const useActivityTracker = () => {
  const location = useLocation();
  const lastTrackedPath = useRef(null);

  // Track page views on location change
  useEffect(() => {
    const currentPath = location.pathname;

    // Avoid duplicate tracking
    if (currentPath === lastTrackedPath.current) return;
    lastTrackedPath.current = currentPath;

    // Determine page title
    let pageTitle = PAGE_TITLES[currentPath];

    // Handle dynamic routes
    if (!pageTitle) {
      if (currentPath.startsWith('/scans/')) {
        pageTitle = 'Details du Scan';
      } else if (currentPath.startsWith('/workspaces/')) {
        pageTitle = 'Details du Workspace';
      } else if (currentPath.startsWith('/vulnerabilities/')) {
        pageTitle = 'Details de la Vulnerabilite';
      } else if (currentPath.startsWith('/sessions/')) {
        pageTitle = 'Details de la Session';
      } else if (currentPath.startsWith('/admin/')) {
        pageTitle = 'Administration';
      } else {
        pageTitle = currentPath;
      }
    }

    trackPageView(currentPath, pageTitle);
  }, [location.pathname]);

  // Track errors globally
  useEffect(() => {
    const handleError = (event) => {
      trackError(
        event.error?.message || event.message || 'Unknown error',
        location.pathname,
        {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        }
      );
    };

    window.addEventListener('error', handleError);
    return () => window.removeEventListener('error', handleError);
  }, [location.pathname]);

  // Flush on unmount/page unload
  useEffect(() => {
    const handleUnload = () => {
      flushActions();
    };

    window.addEventListener('beforeunload', handleUnload);
    return () => {
      window.removeEventListener('beforeunload', handleUnload);
      flushActions();
    };
  }, []);

  // Return tracking functions for manual use
  return {
    trackPageView,
    trackClick,
    trackScanAction,
    trackError,
    trackSearch,
    trackSettingChange,
    trackApiCall,
    trackAction,
    flush: flushActions,
  };
};

/**
 * Higher-order component for tracking
 */
export const withActivityTracking = (WrappedComponent, componentName) => {
  return function TrackedComponent(props) {
    const tracker = useActivityTracker();

    // Track component mount
    useEffect(() => {
      trackAction({
        action_type: 'component_mount',
        action_category: ActionCategories.NAVIGATION,
        description: `Component mounted: ${componentName}`,
        details: { component: componentName },
      });
    }, []);

    return <WrappedComponent {...props} tracker={tracker} />;
  };
};

export default useActivityTracker;
